import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Milestone } from '../types';
import { replacementLabel } from '../utils/milestoneHistory';
import type { TimelineAgreementContext } from '../utils/combinedAgreementTimeline';
import { agreementVariationNumber } from '../utils/agreementIdentity';
import { contractTitle } from '../utils/contractTitle';

export function MilestoneReplacementBadge({ milestone }: { milestone: Milestone }) {
  const { t } = useTranslation();
  return milestone.stageReplacement ? (
    <span className="badge bg-secondary ms-2" data-testid={`milestone-replaced-${milestone.id}`}>
      {replacementLabel(milestone, t)}
    </span>
  ) : null;
}

const contractHref = (projectId: string, contractId: string) =>
  `/projects/${encodeURIComponent(projectId)}?tab=contract&contractId=${encodeURIComponent(contractId)}`;

/** "Öppna ÄTA #n →" and similar: a link in normal case with a 44 px target. */
function CardLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href} className="milestone-ata-card-link">
      {children}
      <i className="feather-arrow-right" aria-hidden="true"></i>
    </a>
  );
}

/**
 * What an ÄTA did to the stage, as a white card in the objection modal's
 * design (ClickUp 869fm6qwf): the ÄTA, where the stage came from and a link
 * to open it.
 */
export default function MilestoneReplacementNotice({ milestone, projectId, amendment, className = 'mb-3' }: {
  milestone: Milestone;
  projectId: string;
  amendment?: TimelineAgreementContext;
  /** Spacing around the card; the stage dialog lays it out itself. */
  className?: string;
}) {
  const { t } = useTranslation();
  const replacement = milestone.stageReplacement;
  const replacementNumber = replacement && amendment?.contract.id === replacement.contractId
    ? agreementVariationNumber(amendment.contract) ?? replacement.contractNumber
    : replacement?.contractNumber;
  const cardClass = ['milestone-ata-card', className].filter(Boolean).join(' ');
  if (!replacement && milestone.continuedBy) return (
    <section className={cardClass} data-testid={`milestone-continuation-${milestone.id}`}>
      <h3 className="milestone-ata-card-title">{t('milestoneHistory.continuedHeading')}</h3>
      <p className="milestone-ata-card-text">{t('milestoneHistory.continuedHint')}</p>
      <CardLink href={contractHref(projectId, milestone.continuedBy.contractId)}>
        {t('milestoneHistory.openCurrentPlan')}
      </CardLink>
    </section>
  );
  if (!replacement && amendment?.contract.stageVariation) {
    const number = agreementVariationNumber(amendment.contract);
    const name = contractTitle(amendment.contract);
    return (
      <section className={cardClass} data-testid={`milestone-amendment-notice-${milestone.id}`}>
        <h3 className="milestone-ata-card-title">
          {name ? t('combinedTimeline.amendmentTitle', { number, name }) : t('combinedTimeline.amendment', { number })}
        </h3>
        {amendment.originalStage && <p className="milestone-ata-card-text">
          <a href={contractHref(projectId, amendment.originalStage.contractId)}>
            {t('milestoneHistory.originalStage', { contract: amendment.originalStage.contractNumber, stage: amendment.originalStage.milestoneNumber })}
          </a>
        </p>}
        <CardLink href={contractHref(projectId, amendment.contract.id)}>
          {t('milestoneHistory.openAmendment', { number })}
        </CardLink>
      </section>
    );
  }
  if (!replacement) return null;
  return (
    <section className={cardClass} data-testid={`milestone-replacement-notice-${milestone.id}`}>
      <h3 className="milestone-ata-card-title">
        {t(replacement.kind === 'removed' ? 'milestoneHistory.removedFromOriginal' : 'milestoneHistory.replaced')}
      </h3>
      <p className="milestone-ata-card-text">{t(replacement.kind === 'removed' ? 'milestoneHistory.removedBy' : 'milestoneHistory.replacedBy', {
        number: replacementNumber,
        name: replacement.contractName || '—'
      })}</p>
      <p className="milestone-ata-card-text">{t('milestoneHistory.historyHint')}</p>
      {replacement.originalStage && (
        <p className="milestone-ata-card-text">
          <a href={contractHref(projectId, replacement.originalStage.contractId)}>
            {t('milestoneHistory.originalStage', { contract: replacement.originalStage.contractNumber, stage: replacement.originalStage.milestoneNumber })}
          </a>
        </p>
      )}
      <div className="milestone-ata-card-links">
        <CardLink href={contractHref(projectId, replacement.contractId)}>
          {t('milestoneHistory.openAmendment', { number: replacementNumber })}
        </CardLink>
        {replacement.currentContractId && (
          <CardLink href={contractHref(projectId, replacement.currentContractId)}>
            {t('milestoneHistory.openCurrentPlan')}
          </CardLink>
        )}
      </div>
    </section>
  );
}
